import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import { Eye, Trash2, FileText, X, Printer, Search, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import ReactDOM from 'react-dom';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';

const ESTADO_FILTERS = [
    { id: 'pendiente', label: 'Pendientes' },
    { id: 'cerrada', label: 'Cerradas' },
    { id: 'todas', label: 'Todas' },
];

export default function PreventasPage() {
    const [preventas, setPreventas] = useState([]);
    const [estadoFilter, setEstadoFilter] = useState('pendiente');
    const [selectedPreventa, setSelectedPreventa] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const hoyChile = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' }).format(new Date());
    const [fechaTickets, setFechaTickets] = useState(hoyChile);
    const [printingDay, setPrintingDay] = useState(false);
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState('recientes');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchPreventas(estadoFilter);
    }, [estadoFilter]);

    const fetchPreventas = async (estado = 'pendiente') => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/preventas?estado=${estado}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPreventas(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPreventaDetails = async (id) => {
        try {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedPreventa(data);
                setShowModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Error al cargar detalles');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta preventa? Esta acción no se puede deshacer.')) return;
        try {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setPreventas(prev => prev.filter(p => p.id !== id));
                if (selectedPreventa?.id === id) setShowModal(false);
            } else {
                alert('No se pudo eliminar la preventa');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const { sorted: sortedPreventaItems, thProps: preventaItemsThProps } = useTableSort(selectedPreventa?.items || [], {
        getters: {
            cantidad: (item) => Number(item.cantidad) || 0,
            precio_unitario: (item) => Number(item.precio_unitario) || 0,
            precio_total: (item) => Number(item.precio_total) || 0,
            product_name: (item) => item.promocion_nombre || item.product_name,
        },
    });

    const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);

    const escapeHtml = (value) => String(value ?? '').replace(/[&<>"]/g, (ch) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]
    ));

    const printPreventasDelDia = async () => {
        setPrintingDay(true);
        try {
            const res = await fetch(
                `${API_URL}/api/preventas?estado=todas&fecha=${fechaTickets}&con_items=1`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            if (!res.ok) throw new Error('No se pudieron cargar las preventas');
            const rows = await res.json();
            if (!Array.isArray(rows) || rows.length === 0) {
                alert('No hay preventas para ese día.');
                return;
            }
            const printWindow = window.open('', '_blank', 'width=420,height=800');
            if (!printWindow) {
                alert('Habilite las ventanas emergentes para imprimir.');
                return;
            }
            const tickets = rows.map((preventa) => {
                const barcode = `PRV${String(preventa.id).padStart(8, '0')}`;
                const items = Array.isArray(preventa.items) ? preventa.items : [];
                const rowsHtml = items.map((item) => `
                    <tr>
                        <td class="qty">${escapeHtml(item.cantidad)}</td>
                        <td class="desc">${escapeHtml(String(item.nombre || '').slice(0, 22))}</td>
                        <td class="price">${formatCurrency(item.precio_total)}</td>
                    </tr>
                `).join('');
                const cuando = preventa.created_at
                    ? new Date(preventa.created_at).toLocaleString('es-CL', { timeZone: 'America/Santiago' })
                    : '';
                return `
                    <section class="ticket">
                        <div class="store">ERP MINIMARKET</div>
                        <div class="banner">NO ES BOLETA · PENDIENTE DE PAGO</div>
                        <div class="title">PREVENTA #${preventa.id}</div>
                        <div class="info">Fecha: ${escapeHtml(cuando)}</div>
                        ${preventa.vendedor_codigo ? `<div class="info">Atendido por: ${escapeHtml(preventa.vendedor_codigo)}</div>` : ''}
                        <table>
                            <thead><tr><th>Cant</th><th>Producto</th><th>Subtotal</th></tr></thead>
                            <tbody>${rowsHtml}</tbody>
                        </table>
                        <div class="total">TOTAL: ${formatCurrency(preventa.sub_total)}</div>
                        <div class="footer">★ PASE A CAJA CON ESTE TICKET ★</div>
                        <div class="barcode-box">
                            <div class="barcode-label">PRESENTE EN CAJA</div>
                            <svg id="barcode-${preventa.id}"></svg>
                            <div class="code">${barcode}</div>
                        </div>
                    </section>
                `;
            }).join('');
            const barcodeScript = rows.map((preventa) => {
                const barcode = `PRV${String(preventa.id).padStart(8, '0')}`;
                return `JsBarcode("#barcode-${preventa.id}", "${barcode}", { format: "CODE128", width: 2, height: 46, displayValue: false, margin: 4 });`;
            }).join('\n');
            printWindow.document.write(`<!DOCTYPE html><html><head><title>Preventas ${fechaTickets}</title>
                <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                <style>
                    @page { margin: 4mm; }
                    body { font-family: Arial, sans-serif; margin: 0; }
                    .ticket { width: 76mm; margin: 0 auto 8mm; page-break-after: always; font-weight: 700; }
                    .ticket:last-child { page-break-after: auto; }
                    .store { text-align: center; font-size: 20px; font-weight: 900; }
                    .banner { text-align: center; background: #111; color: #fff; font-size: 11px; letter-spacing: 0.4px; padding: 6px 4px; margin-top: 8px; }
                    .title { text-align: center; font-size: 20px; border: 3px dashed #000; border-top: none; padding: 8px 4px; letter-spacing: 1px; }
                    .info { font-size: 12px; margin-top: 4px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
                    th { border-bottom: 2px solid #000; text-align: left; }
                    td { padding: 3px 0; vertical-align: top; }
                    .price, th:last-child { text-align: right; }
                    .total { text-align: right; font-size: 18px; border-top: 2px solid #000; margin-top: 8px; padding-top: 6px; }
                    .footer { text-align: center; margin-top: 10px; font-size: 13px; }
                    .barcode-box { margin-top: 10px; text-align: center; border: 2px solid #000; padding: 8px 4px; }
                    .barcode-label { font-size: 12px; letter-spacing: 1px; }
                    .code { font-size: 11px; font-weight: 600; margin-top: 4px; }
                </style></head><body>
                ${tickets}
                <script>
                    window.onload = function() {
                        ${barcodeScript}
                        setTimeout(function() { window.print(); }, 400);
                    };
                </script>
                </body></html>`);
            printWindow.document.close();
        } catch (err) {
            console.error(err);
            alert(err.message || 'No se pudieron imprimir las preventas');
        } finally {
            setPrintingDay(false);
        }
    };
    const formatDate = (dateString) => new Date(dateString).toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'America/Santiago',
    });
    const isPendiente = (p) => p.estado === true;
    const avatarColor = (id) => {
        const colors = ['#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];
        return colors[Number(id) % colors.length];
    };

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        let rows = preventas.filter((preventa) => {
            if (!q) return true;
            return [
                preventa.id,
                preventa.vendedor_name,
                preventa.origen,
                preventa.venta_id,
                preventa.payment_ref,
            ].join(' ').toLowerCase().includes(q);
        });
        rows = [...rows].sort((a, b) => {
            if (sortBy === 'total') return (Number(b.sub_total) || 0) - (Number(a.sub_total) || 0);
            if (sortBy === 'vendedor') return (a.vendedor_name || '').localeCompare(b.vendedor_name || '', 'es');
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return tb - ta || Number(b.id) - Number(a.id);
        });
        return rows;
    }, [preventas, query, sortBy]);

    const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const pageRows = visible.slice((safePage - 1) * pageSize, safePage * pageSize);
    const from = visible.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const to = Math.min(safePage * pageSize, visible.length);
    const selectableIds = pageRows.filter(isPendiente).map((preventa) => preventa.id);
    const allPageSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));

    const toggleAllPage = () => {
        setSelected((prev) => (
            allPageSelected
                ? prev.filter((id) => !selectableIds.includes(id))
                : [...new Set([...prev, ...selectableIds])]
        ));
    };

    const toggleOne = (id) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const deleteMany = async (ids) => {
        const token = localStorage.getItem('token');
        const results = await Promise.all(ids.map(async (id) => {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.ok ? id : null;
        }));
        const removed = results.filter(Boolean);
        setPreventas((prev) => prev.filter((preventa) => !removed.includes(preventa.id)));
        setSelected((prev) => prev.filter((id) => !removed.includes(id)));
        if (removed.includes(selectedPreventa?.id)) setShowModal(false);
    };

    const deleteSelected = async () => {
        const ids = selected.filter((id) => preventas.some((preventa) => preventa.id === id && isPendiente(preventa)));
        if (ids.length === 0) return;
        if (!confirm(`¿Eliminar ${ids.length} preventa${ids.length === 1 ? '' : 's'} pendiente${ids.length === 1 ? '' : 's'}?`)) return;
        try {
            await deleteMany(ids);
        } catch (err) {
            console.error(err);
        }
    };

    const emptyLabel = estadoFilter === 'pendiente'
        ? 'No hay preventas pendientes'
        : estadoFilter === 'cerrada'
            ? 'No hay preventas cerradas'
            : 'No hay preventas registradas';

    return (
        <div className="users-screen">
            <div className="users-head">
                <div className="users-title">
                    <span className="users-title-icon" aria-hidden><FileText size={22} /></span>
                    <div>
                        <h1>Gestión de Preventas</h1>
                        <p>Administra preventas pendientes y consulta el historial de cerradas.</p>
                    </div>
                </div>
            </div>

            <div className="users-card">
                <div className="users-toolbar">
                    <label className="users-search">
                        <Search size={16} aria-hidden />
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                            placeholder="Buscar por número, vendedor o venta..."
                        />
                    </label>
                    <label className="users-filter">
                        <span className="users-dot" aria-hidden />
                        <select
                            value={estadoFilter}
                            onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); setSelected([]); }}
                        >
                            {ESTADO_FILTERS.map((filter) => (
                                <option key={filter.id} value={filter.id}>{filter.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="users-filter">
                        <ArrowUpDown size={15} aria-hidden />
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="recientes">Más recientes</option>
                            <option value="total">Mayor total</option>
                            <option value="vendedor">Vendedor</option>
                        </select>
                    </label>
                    <label className="users-filter">
                        Día
                        <input type="date" value={fechaTickets} onChange={(e) => setFechaTickets(e.target.value)} />
                    </label>
                    <button type="button" className="users-ghost" onClick={printPreventasDelDia} disabled={printingDay || !fechaTickets}>
                        <Printer size={16} />
                        {printingDay ? 'Imprimiendo…' : 'Imprimir tickets'}
                    </button>
                    <button type="button" className="users-danger" onClick={deleteSelected} disabled={selected.length === 0}>
                        <Trash2 size={15} /> Eliminar
                    </button>
                </div>

                {loading ? (
                    <p className="users-empty">Cargando preventas...</p>
                ) : (
                    <div className="users-table-wrap">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} aria-label="Seleccionar página" />
                                    </th>
                                    <th>Preventa</th>
                                    <th>Estado</th>
                                    <th>Origen</th>
                                    <th>Vendedor</th>
                                    <th>Fecha</th>
                                    <th>Items</th>
                                    <th>Total</th>
                                    <th>Venta</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((preventa) => {
                                    const online = preventa.origen === 'venta_online';
                                    const pending = isPendiente(preventa);
                                    return (
                                        <tr key={preventa.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(preventa.id)}
                                                    disabled={!pending}
                                                    onChange={() => toggleOne(preventa.id)}
                                                    aria-label={`Seleccionar preventa ${preventa.id}`}
                                                />
                                            </td>
                                            <td>
                                                <div className="users-person">
                                                    <span className="users-avatar" style={{ background: avatarColor(preventa.id) }}>
                                                        {String(preventa.id).slice(-2)}
                                                    </span>
                                                    <span>
                                                        <strong>Preventa #{preventa.id}</strong>
                                                        <small>{online ? 'Ecommerce' : 'Piso de venta'}</small>
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className="users-status"
                                                    style={pending
                                                        ? { background: '#fff4d6', color: '#b45309' }
                                                        : { background: '#f4f4f5', color: '#71717a' }}
                                                >
                                                    {pending ? 'Pendiente' : 'Cerrada'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className="users-role"
                                                    style={online
                                                        ? { background: '#e9f9ef', color: '#15803d' }
                                                        : { background: '#f4f4f5', color: '#3f3f46' }}
                                                    title={online ? `Pagada online${preventa.payment_ref ? ` · ${preventa.payment_ref}` : ''}` : 'Generada en el POS'}
                                                >
                                                    {online ? 'Venta online' : 'POS'}
                                                </span>
                                            </td>
                                            <td>{online ? 'Ecommerce' : (preventa.vendedor_name || 'Desconocido')}</td>
                                            <td>
                                                <div className="users-access">
                                                    <strong>{formatDate(preventa.created_at)}</strong>
                                                </div>
                                            </td>
                                            <td>{preventa.cantidad_productos} productos</td>
                                            <td><strong>{formatCurrency(preventa.sub_total)}</strong></td>
                                            <td>{preventa.venta_id ? `#${preventa.venta_id}` : '—'}</td>
                                            <td>
                                                <div className="users-actions">
                                                    <button type="button" className="is-edit" title="Ver detalle" onClick={() => fetchPreventaDetails(preventa.id)}>
                                                        <Eye size={16} />
                                                    </button>
                                                    {pending && (
                                                        <button type="button" className="is-danger" title="Eliminar" onClick={() => handleDelete(preventa.id)}>
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pageRows.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="users-empty">{emptyLabel}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="users-footer">
                    <span>Mostrando {from} a {to} de {visible.length} preventas</span>
                    <div className="users-pager">
                        <label>
                            Filas por página:
                            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                            </select>
                        </label>
                        <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={safePage === 1} aria-label="Página anterior">
                            <ChevronLeft size={16} />
                        </button>
                        {Array.from({ length: pageCount }, (_, index) => index + 1).slice(0, 6).map((n) => (
                            <button key={n} type="button" className={n === safePage ? 'is-current' : ''} onClick={() => setPage(n)}>
                                {n}
                            </button>
                        ))}
                        <button type="button" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} disabled={safePage === pageCount} aria-label="Página siguiente">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {showModal && selectedPreventa && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Preventa #{selectedPreventa.id}</h2>
                                <p className="text-sm text-gray-500 flex items-center gap-2 mt-1 flex-wrap">
                                    {isPendiente(selectedPreventa) ? (
                                        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">PENDIENTE</span>
                                    ) : (
                                        <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-semibold">CERRADA</span>
                                    )}
                                    {selectedPreventa.origen === 'venta_online' && (
                                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                            VENTA ONLINE
                                        </span>
                                    )}
                                    <span>• {formatDate(selectedPreventa.created_at)}</span>
                                    <span>
                                        • {selectedPreventa.origen === 'venta_online'
                                            ? 'Origen: Venta Online'
                                            : `Vendedor: ${selectedPreventa.vendedor_name || 'Desconocido'}`}
                                    </span>
                                    {selectedPreventa.venta_id && (
                                        <span>• Venta #{selectedPreventa.venta_id}</span>
                                    )}
                                </p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto">
                            {selectedPreventa.origen === 'venta_online' && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                        <h3 className="text-sm font-semibold text-emerald-900 uppercase tracking-wider">
                                            Venta online ya pagada
                                        </h3>
                                    </div>
                                    <p className="text-xs text-emerald-800/80 mb-3">
                                        El cliente pagó por la web — el cajero solo debe verificar y entregar/despachar.
                                        No vuelva a cobrar.
                                    </p>
                                    <dl className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <dt className="text-emerald-700 font-medium">Pasarela</dt>
                                            <dd className="text-emerald-900">
                                                {selectedPreventa.payment_provider || '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-emerald-700 font-medium">Estado pago</dt>
                                            <dd className="text-emerald-900">
                                                {selectedPreventa.payment_status || '—'}
                                            </dd>
                                        </div>
                                        <div className="col-span-2">
                                            <dt className="text-emerald-700 font-medium">Referencia</dt>
                                            <dd className="text-emerald-900 font-mono break-all">
                                                {selectedPreventa.payment_ref || '—'}
                                            </dd>
                                        </div>
                                    </dl>
                                    {selectedPreventa.nota && (
                                        <div className="mt-3 pt-3 border-t border-emerald-200">
                                            <dt className="text-emerald-700 font-medium text-xs mb-1">Detalle del pedido</dt>
                                            <pre className="whitespace-pre-wrap text-xs text-emerald-900 font-sans">
                                                {selectedPreventa.nota}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Resumen Financiero</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Neto</p>
                                        <p className="font-semibold text-gray-700">{formatCurrency(selectedPreventa.monto_neto)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">IVA (19%)</p>
                                        <p className="font-semibold text-gray-700">{formatCurrency(selectedPreventa.iva)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Total</p>
                                        <p className="font-bold text-indigo-600 text-lg">{formatCurrency(selectedPreventa.sub_total)}</p>
                                    </div>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-gray-800 mb-4">Productos</h3>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                        <tr>
                                            <SortableTh columnKey="codigo_barra" {...preventaItemsThProps} className="px-4 py-3">Código</SortableTh>
                                            <SortableTh columnKey="product_name" {...preventaItemsThProps} className="px-4 py-3">Producto</SortableTh>
                                            <SortableTh columnKey="cantidad" {...preventaItemsThProps} className="px-4 py-3 text-right" align="right">Cantidad</SortableTh>
                                            <SortableTh columnKey="precio_unitario" {...preventaItemsThProps} className="px-4 py-3 text-right" align="right">Precio Unit.</SortableTh>
                                            <SortableTh columnKey="precio_total" {...preventaItemsThProps} className="px-4 py-3 text-right" align="right">Total</SortableTh>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {sortedPreventaItems.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.codigo_barra || '-'}</td>
                                                <td className="px-4 py-3 font-medium text-gray-800">
                                                    {item.promocion_id
                                                        ? (item.promocion_nombre ? `Promo: ${item.promocion_nombre}` : item.product_name)
                                                        : item.product_name}
                                                    {item.gramos ? ` (${item.gramos}g)` : ''}
                                                </td>
                                                <td className="px-4 py-3 text-right">{item.gramos ? `${item.gramos}g` : item.cantidad}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(item.precio_unitario)}</td>
                                                <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(item.precio_total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                                        <tr>
                                            <td colSpan="4" className="px-4 py-3 text-right">Total Final</td>
                                            <td className="px-4 py-3 text-right">{formatCurrency(selectedPreventa.sub_total)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                            {isPendiente(selectedPreventa) && (
                                <button
                                    onClick={() => handleDelete(selectedPreventa.id)}
                                    className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-medium flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash2 size={18} /> Eliminar Preventa
                                </button>
                            )}
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium transition-colors shadow-lg shadow-gray-200"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
